/* ============================================================
 * Jinny Joes — Notify Me / Back-in-Stock (Klaviyo Client API)
 *
 * v3 — robuste Modal-Population:
 *   - Direkter Capture-Phase-Click-Listener populiert sofort
 *   - MutationObserver als Sicherheitsnetz (feuert wenn das
 *     `open` Attribut sich aendert)
 *   - Diagnose-Logs (per ?jjdebug=1 in der URL aktivierbar)
 * ============================================================ */

(function () {
  'use strict';

  var API_REVISION = '2024-10-15';
  var API_URL_BASE = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var SHARED_MODAL_ID = 'JjNotifyMeSharedModal';

  var config = window.jjNotifyMeConfig || {};
  var COMPANY_ID = config.companyId || '';
  var LOCALE = (config.locale || 'de').toLowerCase().slice(0, 2);

  // Debug-Modus: ?jjdebug=1 in der URL aktiviert ausfuehrliche Logs
  var DEBUG = /[?&]jjdebug=1\b/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[jj-notify-me]');
    console.log.apply(console, args);
  }

  var lastTrigger = null;

  function init() {
    log('init() running. COMPANY_ID set:', !!COMPANY_ID, 'LOCALE:', LOCALE);

    if (!COMPANY_ID) {
      console.warn('[jj-notify-me] No Klaviyo Company ID configured.');
      return;
    }

    var modal = document.getElementById(SHARED_MODAL_ID);
    if (!modal) {
      console.warn('[jj-notify-me] Shared modal not found. ID expected:', SHARED_MODAL_ID);
      return;
    }
    log('Shared modal found:', modal);

    // STRATEGY 1: Capture-Phase Click Listener.
    // Feuert garantiert VOR dem Theme-Modal-Handler (der ist
    // Bubble-Phase via ftdomdelegate). Populiert das Modal sofort
    // beim Klick.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.jj-notify-me__trigger');
      if (!trigger) return;

      log('CLICK on trigger:', trigger);
      log('  data-product-title:', trigger.getAttribute('data-product-title'));
      log('  data-product-image:', trigger.getAttribute('data-product-image'));
      log('  data-variant-id:', trigger.getAttribute('data-variant-id'));
      log('  data-variant-title:', trigger.getAttribute('data-variant-title'));

      lastTrigger = trigger;
      populateModal(modal, trigger);
    }, true); // <-- capture phase

    // STRATEGY 2: MutationObserver auf das `open` Attribut.
    // Sicherheitsnetz falls Strategy 1 nicht greift.
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'open' && modal.hasAttribute('open') && lastTrigger) {
          log('MUTATION observed: modal opened. Re-populating.');
          populateModal(modal, lastTrigger);
        }
      });
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['open'] });

    setupModalForm(modal);
    log('Initialization complete.');
  }

  function populateModal(modal, trigger) {
    var titleEl = modal.querySelector('[data-modal-title]');
    var variantEl = modal.querySelector('[data-modal-variant]');
    var imageEl = modal.querySelector('[data-modal-image]');

    log('populateModal called. Elements found:',
        'title=', !!titleEl,
        'variant=', !!variantEl,
        'image=', !!imageEl);

    var productTitle = trigger.getAttribute('data-product-title') || '';
    var variantTitle = trigger.getAttribute('data-variant-title') || '';
    var productImage = trigger.getAttribute('data-product-image') || '';

    if (titleEl) {
      titleEl.textContent = productTitle;
      log('  -> title set to:', productTitle);
    }

    if (variantEl) {
      var v = (variantTitle && variantTitle !== 'Default Title') ? variantTitle : '';
      variantEl.textContent = v;
      log('  -> variant set to:', v || '(empty)');
    }

    if (imageEl) {
      if (productImage) {
        // Protocol-relative URLs muessen mit https: prefixed werden
        var imgSrc = productImage.indexOf('//') === 0 ? 'https:' + productImage : productImage;
        imageEl.src = imgSrc;
        imageEl.alt = productTitle;
        imageEl.hidden = false;
        log('  -> image src set to:', imgSrc);
      } else {
        imageEl.hidden = true;
        log('  -> image hidden (no src)');
      }
    }

    resetModalState(modal);
  }

  function resetModalState(modal) {
    var formWrapper = modal.querySelector('[data-state="form"]');
    var successWrapper = modal.querySelector('[data-state="success"]');
    var errorEl = modal.querySelector('[data-error]');
    var emailInput = modal.querySelector('.jj-notify-me__input');
    var submitBtn = modal.querySelector('.jj-notify-me__submit');
    var submitLabel = modal.querySelector('.jj-notify-me__submit-label');
    var submitLoading = modal.querySelector('.jj-notify-me__submit-loading');

    if (formWrapper) formWrapper.removeAttribute('hidden');
    if (successWrapper) successWrapper.setAttribute('hidden', '');
    if (errorEl) {
      errorEl.setAttribute('hidden', '');
      errorEl.textContent = '';
    }
    if (emailInput) emailInput.value = '';
    if (submitBtn) submitBtn.disabled = false;
    if (submitLabel) submitLabel.removeAttribute('hidden');
    if (submitLoading) submitLoading.setAttribute('hidden', '');

    setTimeout(function () { emailInput && emailInput.focus(); }, 150);
  }

  function setupModalForm(modal) {
    var form = modal.querySelector('.jj-notify-me__form');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      handleSubmit(modal);
    });
  }

  function handleSubmit(modal) {
    var emailInput = modal.querySelector('.jj-notify-me__input');
    var email = (emailInput.value || '').trim();

    if (!isValidEmail(email)) {
      showError(modal, 'error_email_invalid');
      return;
    }

    if (!lastTrigger) {
      log('Submit blocked: no lastTrigger.');
      showError(modal, 'error_generic');
      return;
    }

    var variantId = lastTrigger.getAttribute('data-variant-id');
    if (!variantId) {
      log('Submit blocked: no variantId on trigger.');
      showError(modal, 'error_generic');
      return;
    }

    var productUrl = lastTrigger.getAttribute('data-product-url') || window.location.href;
    log('Submit: email=', email, 'variantId=', variantId);

    setLoading(modal, true);
    var errorEl = modal.querySelector('[data-error]');
    if (errorEl) errorEl.setAttribute('hidden', '');

    // Schritt 1: Profile-Properties setzen (language, source_url)
    // Wichtig: VOR dem BIS-Subscribe, damit die Properties am
    // Profil sind, wenn das "Subscribed to Back in Stock" Event
    // ausgeloest wird.
    identifyProfile({ email: email, language: LOCALE, source_url: productUrl });

    // Schritt 2: BIS-Subscription erstellen
    subscribe({ email: email, variantId: String(variantId), language: LOCALE, sourceUrl: productUrl })
      .then(function () {
        showSuccess(modal);
      })
      .catch(function (err) {
        console.error('[jj-notify-me] Subscription failed:', err);
        setLoading(modal, false);
        showError(modal, 'error_generic');
      });
  }

  function setLoading(modal, isLoading) {
    var submitBtn = modal.querySelector('.jj-notify-me__submit');
    var submitLabel = modal.querySelector('.jj-notify-me__submit-label');
    var submitLoading = modal.querySelector('.jj-notify-me__submit-loading');

    if (!submitBtn) return;
    submitBtn.disabled = isLoading;
    if (submitLabel && submitLoading) {
      if (isLoading) {
        submitLabel.setAttribute('hidden', '');
        submitLoading.removeAttribute('hidden');
      } else {
        submitLabel.removeAttribute('hidden');
        submitLoading.setAttribute('hidden', '');
      }
    }
  }

  function showError(modal, messageKey) {
    var errorEl = modal.querySelector('[data-error]');
    if (!errorEl) return;
    var messages = (window.jjNotifyMeMessages || {});
    errorEl.textContent = messages[messageKey] || messages.error_generic || 'Something went wrong.';
    errorEl.removeAttribute('hidden');
  }

  function showSuccess(modal) {
    var formWrapper = modal.querySelector('[data-state="form"]');
    var successWrapper = modal.querySelector('[data-state="success"]');
    if (formWrapper) formWrapper.setAttribute('hidden', '');
    if (successWrapper) successWrapper.removeAttribute('hidden');
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function subscribe(opts) {
    // Klaviyos BIS-Endpoint akzeptiert im profile.attributes NUR
    // Standard-Felder (email, phone_number, external_id).
    // Custom Properties (language, source_url) werden separat
    // ueber identifyProfile() gesetzt — siehe handleSubmit().
    var payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: opts.email
              }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: { type: 'catalog-variant', id: '$shopify:::$default:::' + opts.variantId }
          }
        }
      }
    };

    return fetch(API_URL_BASE + '?company_id=' + encodeURIComponent(COMPANY_ID), {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
        'revision': API_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (response.status >= 200 && response.status < 300) return response;
      return response.text().then(function (text) {
        throw new Error('Klaviyo BIS API error ' + response.status + ': ' + text);
      });
    });
  }

  function identifyProfile(props) {
    // Custom Properties auf das Profil setzen via Klaviyos
    // Onsite-JS. Drei Pfade fuer maximale Robustheit:
    //   1. window.klaviyo.identify (moderne API, wenn voll geladen)
    //   2. window._learnq.push (legacy, funktioniert auch mit
    //      Array-Queue VOR dem Laden der Klaviyo-JS)
    //   3. Eigenes Array-Queue anlegen falls noch nichts da ist
    try {
      var data = {
        $email: props.email,
        language: props.language,
        source_url: props.source_url
      };

      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify(data);
        log('identifyProfile: via window.klaviyo.identify');
      } else {
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', data]);
        log('identifyProfile: via _learnq queue (length now:', window._learnq.length, ')');
      }
    } catch (e) {
      console.warn('[jj-notify-me] identify call failed (non-critical):', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
